import app from '../appContext';
import * as tools from '../utils/tools';
import EventEmitter from '../support/eventemitter';

// TODO: Make NPM Provider URL configurable per Coactive environment (for now, an environment could execute api.utils.npmProviderUrl = '...')
// Used to load npm modules via CDN...other alternatives follow...from https://github.com/konklone/cdns-to-https/issues/10
const defaultNpmProviderUrl = "https://unpkg.com/";
// wzrd.in/standalone
// unpkg.com (npmcdn.com)
// cdnjs.cloudflare.com
// cdn.jsdelivr.net
// ajax.aspnetcdn.com

export class Utils extends EventEmitter {
    constructor() {
        super();
        this._npmProviderUrl;
    }

    /**
     * Get the environment's current npm package provider URL.
     * @return {string} The URL prefix that is used to load npm packages when calling api.utils.loadResource().
     */
    get npmProviderUrl () {
        return this._npmProviderUrl || defaultNpmProviderUrl;
    }

    /**
     * Set the environment's npm package provider URL.
     * @param  {string} value The URL prefix that will be used to load npm packages when calling api.utils.loadResource().
     */
    set npmProviderUrl (value) {
        if (value) {
            value = value.toLowerCase();
            if (value.startsWith("http://")) {
                // Ensure we're using SSL
                value = value.replace("http://", "https://");
            } else if (!value.startsWith("https://")) {
                // Ensure protocol exists and is secure
                value = "https://" + value;
            }
            if (!value.endsWith("/")) {
                // Ensure URL ends with a slash since npm package name will be appended to form full URL
                value = value + "/";
            }
            this._npmProviderUrl = value;
        }
    }

    /**
     * Returns the name of the specified Action Chain from the specified View.
     * @param {Object|Number} viewID - The ID or linker object of the view to search.
     * @param {Number} actionChainID - The ID of the action chain to find.
     * @param {String} [nameIfNull] - A string to use if the view or action chain is not found.
     * @returns {String|null} The task name if it was found, or the specified default string, or null
     */
    getActionChainNameFromView (viewID, actionChainID, nameIfNull) {
        console.warn('DEPRECATED: use api.actionHelpers.getTaskNameFromForm instead of api.utils.getActionChainNameFromView');
        return api.actionHelpers.getTaskNameFromForm(viewID, actionChainID, nameIfNull);
    }

    /**
     * Gets a Property object from the cache using it's PropertyID
     * @param {Object|Number} propertyID - The PropertyID or linker object of the property to find.
     * @returns {Object|null} The property if it was found
     */
    getPropertyByID (propertyID) {
        console.warn('DEPRECATED: use api.entity.getColumnByID instead of api.utils.getPropertyByID. Please note that getColumnByID will return an API entity wrapper object');
        let ret = api.entity.getColumnByID(propertyID);
        return (ret != null) ? ret.raw : null;
    }

    /**
     * Returns the Title of the specified property.
     * @param {Object|Number} propertyID - The ID or linker object of the property to find.
     * @param {String} [titleIfNull] - The string to use if the property is not found.
     * @returns {String} The title of the specified property.
     */
    getPropertyTitleByID (propertyID, titleIfNull) {
        console.warn('DEPRECATED: use api.entity.getColumnTitleByID instead of api.utils.getPropertyTitleByID');
        return api.entity.getColumnTitleByID(propertyID, titleIfNull);
    }

    /**
     * Replaces the Property IDs in the raw expression text with their corresponding field names.
     * @param {String} rawExpression - The raw expression text.
     */
    displayExpression (rawExpression){
        console.warn('DEPRECATED: use api.expressionHelpers.displayExpression instead of api.utils.displayExpression');
        // Call the utility function to replace field IDs with names..PW
        return api.expressionHelpers.displayExpression(rawExpression);
    }

    /**
     * Ensures that the passed object is a function and invokes it.
     * @param {object} fn       The function to validate and invoke.
     * @param {object} thisArg  The value to use as `this` when the function is executed.
     * @param {array}  params   The parameters to pass to the function.
     * @returns {any}           The return value of the function if invoked.
     */
    runFunction  (fn, thisArg, params) {
        if (fn && typeof fn === "function") {
            return fn.apply(thisArg || window, params);
        }
    }

    /**
     * Runs the specified JavaScript on the client.
     * @param {string} jsToExecute    The JavaScript to be evaluated and executed locally.
     */
    runJavaScript  (jsToExecute) {
        // Inject the //runAs JavaScript header to evaluate the expression as JS
        jsToExecute = "//runAs JavaScript\n" + jsToExecute;

        // Create an expression object
        const exprToExecute = new app.dv.helpers.expression.create(null, null, jsToExecute, null, null);

        // Evaluate the expression to execute the JavaScript
        return app.dv.helpers.exprEng.evaluate(exprToExecute);
    }

    /**
     * Loads the specified resource (JS or CSS) from a URL.
     * @param {string}      url             The URL of the resource.
     * @param {string}      resourceType    A valid resource type ('javascript', 'js', 'stylesheet', 'css', 'npm')
     * @param {function}    callback        A function to be called after the resource has been loaded.
     * @param {string}      rawContent      Raw content (e.g. the actual JavaScript or CSS) to be loaded in addition to or in lieu of content from a URL.
     *                                      For npm, or if a URL is specified for CSS, any raw content will be ignored.
     * @return {DOMElement}                 The element that was created, or if one already existed for the resource, the existing element.
     */
    loadResource (url, resourceType, callback, rawContent) {
        const resourceTypeLC = resourceType.toLowerCase();
        let elementSourceProperty = null;
        // Function to create a new element of the specified kind
        const createElement = (elementKind, type) => {
            const element = document.createElement(elementKind);
            element.type = type;
            // Generate what should be a unique ID based on the URL/raw content
            element.id = window.btoa(url) || api.utils.generateHash(rawContent);
            return element;
        };

        // Create an element of the appropriate kind based on the resource type specified (JS or CSS)
        let newElement = null;
        switch (true) {
            case resourceTypeLC.indexOf("javascript") > -1 || resourceTypeLC.indexOf("js") > -1:
                newElement = createElement("script", "text/javascript");
                if (url) newElement.src = url;
                elementSourceProperty = "src";
                break;

            case resourceTypeLC.indexOf("npm") > -1:
                newElement = createElement("script", "text/javascript");
                newElement.src = "" + (url.startsWith("http://") || url.startsWith("https://") ? "" : api.utils.npmProviderUrl) + url;
                elementSourceProperty = "src";
                break;

            case resourceTypeLC.indexOf("stylesheet") > -1 || resourceTypeLC.indexOf("css") > -1:
                if (url) {
                    newElement = createElement("link", "text/css");
                    newElement.href = url;
                    newElement.rel = "stylesheet";
                    elementSourceProperty = "href";
                } else if (rawContent) {
                    newElement = createElement("style", "text/css");
                    elementSourceProperty = "xxx-not-used";
                }
                break;

            default:
                console.log("WARNING: resourceType [" + resourceType + "] not supported for api.utils.loadResource.");
                break;
        }

        if (newElement) {
            // Inject raw content if any provided
            if (rawContent) newElement.innerHTML = rawContent;
            // Prevent duplicate loading of the same resource and type
            const foundElement = [...document.getElementsByTagName(newElement.tagName)]
                .find(element => element.type === newElement.type && element[elementSourceProperty] === url);
            if (foundElement) {
                // Just call back if such an element was already loaded
                newElement = foundElement;
                api.utils.runFunction(callback);
            } else {
                // Set up appropriate event handler to call back when the element has been loaded
                if (newElement.readyState) {
                    //IE
                    newElement.onreadystatechange = function () {
                        if (newElement.readyState === "loaded" || newElement.readyState === "complete") {
                            newElement.onreadystatechange = null;
                            api.utils.runFunction(callback);
                        }
                    };
                } else if (url) {
                    //Others
                    newElement.onload = function () {
                        api.utils.runFunction(callback);
                    };
                } else if (rawContent) {
                    // Only raw content was provided, so just wait a tick and call back
                    window.setTimeout(function() {
                        api.utils.runFunction(callback);
                    });
                }

                // Add the new element to the document
                document.head.appendChild(newElement);
            }
        }
        return newElement;
    }

    /**
     * Generates a unique hash based on a seed value.
     * @param   {any}       value       The value to be used for hash generation.
     * @return  {number}                A numeric hash unique to the seed value.
     */
    generateHash (value) {
        if (typeof value !== "string") {
            value = value.toString();
        }
        let hash = 5381;
        let i = value.length;
        while(i) {
            hash = (hash * 33) ^ value.charCodeAt(--i);
        }
        /* JavaScript does bitwise operations (like XOR, above) on 32-bit signed
       * integers. Since we want the results to be always positive, convert the
       * signed int to an unsigned by doing an unsigned bitshift. */
        return hash >>> 0;
    }

    /**
     * Determines if current browser supports ES6
     * @return {boolean}    True if browser supports ES6; otherwise false.
     */
    supportsES6 () {
        try {
            new Function("(a = 0) => a");
            return true;
        }
        catch (err) {
            return false;
        }
    }
}
